import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, ShieldCheck, Truck, Syringe } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { resolveImage } from "../lib/api";
import { Button } from "./ui/button";

const Hero = () => {
  const { content } = useContent();
  const slides = content.heroSlides || [];
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) {
    return <section id="home" className="h-[88vh] bg-[#b91c1c]" />;
  }

  const safeIdx = Math.min(current, slides.length - 1);
  const goPrev = () => setCurrent((c) => (c - 1 + slides.length) % slides.length);
  const goNext = () => setCurrent((c) => (c + 1) % slides.length);

  return (
    <section id="home" className="relative h-[88vh] min-h-[600px] w-full overflow-hidden bg-[#b91c1c]">
      {slides.map((slide, i) => (
        <div key={slide.id || i} className={`absolute inset-0 transition-opacity duration-1000 ${i === safeIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          {slide.image && (
            <img
              src={resolveImage(slide.image)}
              alt={`${slide.title} - Aktav Yarka`}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
              decoding="async"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#b91c1c]/95 via-[#b91c1c]/75 to-[#b91c1c]/40" />
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur border border-white/20 rounded-full text-white text-xs font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              15+ Yıllık Tecrübe • Aşı Garantili
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white leading-[1.05] mb-6 tracking-tight">{slides[safeIdx].title}</h1>
            <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-xl">{slides[safeIdx].subtitle}</p>
            <div className="flex flex-wrap gap-4 mb-12">
              <Button asChild size="lg" className="bg-white text-[#b91c1c] hover:bg-white/90 font-semibold px-7 h-12">
                <a href="#contact">{slides[safeIdx].cta} <ArrowRight className="w-4 h-4 ml-2" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white font-semibold px-7 h-12">
                <a href="#products">Ürünleri İncele</a>
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-xl">
              {[{ icon: Syringe, label: "Aşı Garantili" }, { icon: Truck, label: "Hızlı Teslimat" }, { icon: ShieldCheck, label: "Veteriner Onaylı" }].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-white/90">
                  <div className="w-9 h-9 rounded-md bg-white/10 border border-white/20 flex items-center justify-center">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <span className="text-xs md:text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={goPrev} className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white hover:text-[#b91c1c] transition-colors" aria-label="Önceki">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={goNext} className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white hover:text-[#b91c1c] transition-colors" aria-label="Sonraki">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setCurrent(idx)} className={`h-1.5 rounded-full transition-all ${idx === safeIdx ? "w-10 bg-white" : "w-2.5 bg-white/40"}`} aria-label={`Slayt ${idx + 1}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default Hero;
